<template>
  <v-navigation-drawer
    id="main-navigation"
    v-model="drawerOpen"
    :mobile-breakpoint="1280"
    :temporary="isMobile"
    :permanent="!isMobile"
    :rail="isRail"
    :width="272"
    :rail-width="72"
    :aria-label="$t('NAV_MAIN')"
    :aria-hidden="isMobile && !drawerOpen ? 'true' : undefined"
    :inert="isMobile && !drawerOpen"
    class="tower-navigation"
    @keydown="handleNavigationKeydown"
  >
    <template #prepend>
      <div class="navigation-brand">
        <v-avatar size="32" color="transparent"
          ><v-img :src="logoUrl" alt=""
        /></v-avatar>
        <span v-if="!isRail" class="navigation-brand__name">Tower</span>
        <v-btn
          v-if="isMobile"
          ref="closeNavigationButton"
          :aria-label="$t('NAV_CLOSE')"
          icon="fas fa-xmark"
          variant="text"
          size="small"
          class="ml-auto"
          @click="closeNavigation"
        />
      </div>
      <v-divider />
    </template>

    <v-list
      nav
      role="presentation"
      tabindex="-1"
      density="compact"
      class="navigation-list"
    >
      <template v-for="(group, index) in navigationGroups" :key="group.key">
        <v-divider v-if="isRail && index" class="my-3" />
        <v-list-subheader v-if="!isRail" class="navigation-heading">{{
          $t(group.key)
        }}</v-list-subheader>
        <v-tooltip
          v-for="link in group.links"
          :key="link.route"
          :text="link.name"
          location="end"
          :disabled="!isRail"
        >
          <template #activator="{ props }">
            <v-list-item
              v-bind="props"
              :to="{ name: link.route, hash: link.hash }"
              :active="isActive(link)"
              :aria-current="isActive(link) ? 'page' : undefined"
              :aria-label="link.name"
              tabindex="0"
              color="primary"
              rounded="lg"
              class="navigation-link"
              @click="onNavigate"
            >
              <template #prepend>
                <v-badge
                  :model-value="!!link.badge && isRail"
                  :content="link.badge"
                  color="error"
                  floating
                >
                  <v-icon :icon="link.icon" size="18" />
                </v-badge>
              </template>
              <v-list-item-title v-if="!isRail">{{
                link.name
              }}</v-list-item-title>
              <template v-if="link.badge && !isRail" #append>
                <v-badge :content="link.badge" color="error" inline />
              </template>
            </v-list-item>
          </template>
        </v-tooltip>
      </template>
    </v-list>
  </v-navigation-drawer>

  <v-app-bar flat class="app-bar-modern" elevation="0" height="64">
    <v-btn
      ref="navigationToggle"
      :icon="
        isMobile
          ? 'fas fa-bars'
          : isRail
          ? 'fas fa-angles-right'
          : 'fas fa-angles-left'
      "
      :aria-label="toggleLabel"
      :aria-expanded="isMobile ? drawerOpen : !isRail"
      aria-controls="main-navigation"
      variant="text"
      class="ml-2"
      @click="toggleNavigation"
    />
    <v-app-bar-title class="navigation-page-title">{{
      activePageTitle
    }}</v-app-bar-title>
    <!-- Language picker -->
    <v-menu>
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          :aria-label="$t('NAV_LANGUAGE')"
          variant="text"
          size="small"
          class="mx-1"
        >
          <img
            :src="langs[$i18n.locale]"
            width="22"
            :alt="$i18n.locale"
            style="border-radius: 3px"
          />
        </v-btn>
      </template>
      <v-list rounded="lg" density="compact" class="pa-1">
        <v-list-item
          v-for="locale in $i18n.availableLocales"
          :key="`locale-${locale}`"
          :value="locale"
          @click="changeLocale(locale)"
          rounded="lg"
        >
          <v-list-item-title>
            <img
              :src="langs[locale]"
              width="22"
              :alt="locale"
              style="border-radius: 3px"
            />
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Theme toggle -->
    <v-btn
      :aria-label="$t($vuetify.theme.current.dark ? 'GO_LIGHT' : 'GO_DARK')"
      :icon="$vuetify.theme.current.dark ? 'fas fa-sun' : 'fas fa-moon'"
      variant="text"
      size="small"
      @click="toggleTheme"
      class="mx-1"
    ></v-btn>

    <!-- User menu -->
    <v-menu>
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          :aria-label="$t('NAV_ACCOUNT')"
          icon="fas fa-user"
          variant="tonal"
          size="small"
          class="mr-4 ml-1"
        />
      </template>
      <v-list rounded="lg" density="compact" class="pa-1">
        <v-list-item rounded="lg" @click="signout">
          <template v-slot:prepend>
            <v-icon size="14" class="mr-2">fas fa-sign-out-alt</v-icon>
          </template>
          <v-list-item-title class="text-body-2">{{
            $t('SIGN_OUT')
          }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-app-bar>
</template>

<script>
import { useDisplay, useTheme } from 'vuetify'
import { setLocale, setTheme } from '@/utils'
import { navigationGroups, matchesNavigation } from '@/navigation'

export default {
  name: 'AppBar',
  setup() {
    const { mdAndDown: isMobile } = useDisplay()
    const theme = useTheme()
    function toggleTheme() {
      const newTheme = theme.global.current.value.dark ? 'light' : 'dark'
      setTheme(newTheme)
      theme.global.name.value = newTheme
    }
    return { isMobile, toggleTheme }
  },
  data: () => ({
    drawerOpen: false,
    collapsed: false,
    logoUrl: require('@/assets/logo.png'),
    langs: {
      en: require('@/assets/lang/en-US.svg'),
      'es-MX': require('@/assets/lang/es-MX.svg'),
    },
  }),
  computed: {
    isRail() {
      return !this.isMobile && this.collapsed
    },
    navigationGroups() {
      return navigationGroups
        .map((group) => ({
          ...group,
          links: group.links
            .filter(
              (link) =>
                !link.role ||
                this.currentUser.isDeveloper ||
                (link.role === 'mainAdmin' && this.currentUser.isMainAdmin)
            )
            .map((link) => ({
              ...link,
              name: link.plural ? this.$tc(link.key, 2) : this.$t(link.key),
              badge:
                link.route === 'supportTickets'
                  ? this.$store.getters.unreadTicketsCount
                  : 0,
            })),
        }))
        .filter((group) => group.links.length)
    },
    activePageTitle() {
      return (
        this.navigationGroups
          .flatMap((group) => group.links)
          .find((link) => this.isActive(link))?.name || 'Tower'
      )
    },
    toggleLabel() {
      return this.$t(
        this.isMobile
          ? this.drawerOpen
            ? 'NAV_CLOSE'
            : 'NAV_OPEN'
          : this.isRail
          ? 'NAV_EXPAND'
          : 'NAV_COLLAPSE'
      )
    },
  },
  watch: {
    isMobile: {
      immediate: true,
      handler(value) {
        this.drawerOpen = !value
      },
    },
    '$route.fullPath'() {
      if (this.isMobile) this.closeNavigation()
    },
  },
  methods: {
    isActive(link) {
      return matchesNavigation(link, this.$route.path)
    },
    toggleNavigation() {
      if (!this.isMobile) {
        this.collapsed = !this.collapsed
        return
      }
      if (this.drawerOpen) this.closeNavigation()
      else {
        this.drawerOpen = true
        this.$nextTick(() => this.$refs.closeNavigationButton?.$el.focus())
      }
    },
    closeNavigation() {
      this.drawerOpen = false
      this.$nextTick(() => this.$refs.navigationToggle?.$el.focus())
    },
    onNavigate() {
      if (this.isMobile) this.closeNavigation()
    },
    handleNavigationKeydown(event) {
      if (!this.isMobile || !this.drawerOpen) return
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeNavigation()
      }
      if (event.key === 'Tab') {
        const elements = [
          ...event.currentTarget.querySelectorAll('button, a[href]'),
        ].filter((el) => !el.disabled && el.tabIndex >= 0)
        const first = elements[0]
        const last = elements[elements.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last?.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first?.focus()
        }
      }
    },
    signout() {
      this.logout()
      this.goTo('login')
    },
    changeLocale(locale) {
      setLocale(locale)
      this.$i18n.locale = locale
    },
  },
  mounted() {
    this.fetchCurrentUser()
  },
}
</script>

<style scoped>
.app-bar-modern {
  border-bottom: 1px solid rgba(var(--v-border-color), 0.08) !important;
}
.navigation-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 64px;
  padding: 0 20px;
}
.navigation-brand__name {
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}
.navigation-list {
  padding: 12px;
}
.navigation-heading {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  min-height: 36px;
  margin-top: 8px;
}
.navigation-link {
  min-height: 44px;
  margin-bottom: 4px;
}
.navigation-link :deep(.v-list-item-title) {
  font-size: 0.875rem;
  font-weight: 500;
  white-space: normal;
  line-height: 1.35;
}
.navigation-link :deep(.v-list-item__prepend > .v-list-item__spacer) {
  width: 16px;
}
.navigation-link:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
}
.navigation-page-title {
  font-size: 1rem;
  font-weight: 600;
  margin-inline-start: 8px;
}
</style>
